import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from '../../utils/axiosInstance';
import { Attachment } from '../types';

export interface AttachmentListFilters {
  campaign?: string;
  brand?: string;
  task?: string;
  bf_terms?: number;
}

const listKey = (f: AttachmentListFilters) => ['attachments', f] as const;

const listAttachments = async (f: AttachmentListFilters): Promise<Attachment[]> => {
  const params = new URLSearchParams();
  if (f.campaign) params.set('campaign', f.campaign);
  if (f.brand) params.set('brand', f.brand);
  if (f.task) params.set('task', f.task);
  if (f.bf_terms) params.set('bf_terms', String(f.bf_terms));
  const q = params.toString() ? `?${params.toString()}` : '';
  const res = await axios.get(`api/attachments/${q}`);
  return res.data;
};

export const useAttachments = (f: AttachmentListFilters) =>
  useQuery({
    queryKey: listKey(f),
    queryFn: () => listAttachments(f),
    enabled: !!(f.campaign || f.brand || f.task || f.bf_terms),
  });

export interface UploadAttachmentInput {
  file: File;
  campaign?: string;
  brand?: string;
  task?: string;
  bf_terms?: number;
}

export const useUploadAttachment = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ file, campaign, brand, task, bf_terms }: UploadAttachmentInput): Promise<Attachment> => {
      const form = new FormData();
      form.append('file', file);
      if (campaign) form.append('campaign', campaign);
      if (brand) form.append('brand', brand);
      if (task) form.append('task', task);
      if (bf_terms) form.append('bf_terms', String(bf_terms));
      const res = await axios.post('api/attachments/', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['attachments'] }),
  });
};

export const useDeleteAttachment = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await axios.delete(`api/attachments/${id}/`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['attachments'] }),
  });
};

export const downloadAttachment = async (att: Attachment): Promise<void> => {
  const res = await axios.get(`api/attachments/${att.id}/download/`, {
    responseType: 'blob',
  });
  const url = window.URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = att.name;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
};
